/**
 * Maintenance Repository
 * COMPLETE implementation for Equipment, MaintenanceRecords, and WorkOrders
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Equipment, MaintenanceRecord, WorkOrder } from '../../../types/database';

export class EquipmentRepository extends BaseRepository<Equipment> {
  constructor() {
    super(db.equipment, 'equipment');
  }

  async findByCategory(category: string): Promise<Equipment[]> {
    return await this.table.where('category').equals(category).toArray();
  }

  async findByStatus(status: Equipment['status']): Promise<Equipment[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findCritical(): Promise<Equipment[]> {
    return await this.table.where('criticalityLevel').equals('critical').toArray();
  }

  async findByLocation(location: string): Promise<Equipment[]> {
    return await this.table.where('location').equals(location).toArray();
  }

  async search(searchTerm: string): Promise<Equipment[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(eq =>
      eq.equipmentName.toLowerCase().includes(term) ||
      eq.equipmentCode.toLowerCase().includes(term) ||
      (eq.manufacturer !== undefined && eq.manufacturer.toLowerCase().includes(term)) ||
      (eq.model !== undefined && eq.model.toLowerCase().includes(term))
    ).toArray();
  }
}

export class MaintenanceRecordRepository extends BaseRepository<MaintenanceRecord> {
  constructor() {
    super(db.maintenanceRecords, 'maintenanceRecords');
  }

  async getByOrganization(organizationId: string): Promise<MaintenanceRecord[]> {
    return await this.table.where('organizationId').equals(organizationId).toArray();
  }

  async findByEquipment(equipmentId: string): Promise<MaintenanceRecord[]> {
    return await this.table.where('equipmentId').equals(equipmentId).toArray();
  }

  async findByStatus(status: MaintenanceRecord['status']): Promise<MaintenanceRecord[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<MaintenanceRecord[]> {
    return await this.table
      .where('performedDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async getLatestForEquipment(equipmentId: string): Promise<MaintenanceRecord | undefined> {
    const records = await this.findByEquipment(equipmentId);
    return records
      .filter(r => r.status === 'completed')
      .sort((a, b) => new Date(b.performedDate).getTime() - new Date(a.performedDate).getTime())[0];
  }
}

export class WorkOrderRepository extends BaseRepository<WorkOrder> {
  constructor() {
    super(db.workOrders, 'workOrders');
  }

  async findByEquipment(equipmentId: string): Promise<WorkOrder[]> {
    return await this.table.where('equipmentId').equals(equipmentId).toArray();
  }

  async findByStatus(status: WorkOrder['status']): Promise<WorkOrder[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findByPriority(priority: WorkOrder['priority']): Promise<WorkOrder[]> {
    return await this.table.where('priority').equals(priority).toArray();
  }

  async findOverdue(): Promise<WorkOrder[]> {
    const now = new Date();
    const openOrders = await this.table
      .where('status')
      .anyOf(['open', 'assigned', 'in_progress'])
      .toArray();
    
    return openOrders.filter(wo => 
      wo.scheduledDate && new Date(wo.scheduledDate) < now
    );
  }

  async getOpen(): Promise<WorkOrder[]> {
    return await this.table
      .where('status')
      .anyOf(['open', 'assigned', 'in_progress'])
      .toArray();
  }
}

export const equipmentRepository = new EquipmentRepository();
export const maintenanceRecordRepository = new MaintenanceRecordRepository();
export const workOrderRepository = new WorkOrderRepository();


